import type {
  AchievementShowcaseDTO,
  CCCOfferDTO,
  ChatMessageDTO,
  GameConfig,
  GameSnapshot,
  VictoryRevealEntry,
  VictoryVoteCounts,
  WordListMeta,
  WordOfferDTO,
  WordView,
} from "../../shared/types";

/** An achievement announced in the victory ceremony, plus the players whose dots
 * have appeared on it so far (filled in one at a time as they're revealed). */
export type VictoryAchievement = AchievementShowcaseDTO & { playerIds: string[] };

export interface AppState {
  connected: boolean;
  lobbyCode: string | null;
  myPlayerId: string | null;
  isHost: boolean;
  isSpectator: boolean;
  joined: boolean;
  /** Chose "watch as anonymous Spectator" from the join screen - no Player
   * identity at all, so this is purely a client-side view-mode flag (never
   * persisted; a reload just drops back to the join screen). Only meaningful
   * while a game is in progress - see mainArea.ts's computeViewKey. */
  isAnonymousSpectator: boolean;
  snapshot: GameSnapshot | null;
  wordView: WordView | null;
  wordOffer: WordOfferDTO | null;
  cccOffer: CCCOfferDTO | null;
  config: GameConfig | null;
  chatMessages: ChatMessageDTO[];
  /** Whether the CCC debug-pick testing aid is even available in this
   * deployment - a server-wide env flag, not a lobby setting. */
  cccDebugAvailable: boolean;
  /** Every word list the server has loaded - server-wide, not a lobby setting.
   * Seeded with just "English" until the real server:info broadcast arrives. */
  availableWordLists: WordListMeta[];
  victoryAchievements: VictoryAchievement[];
  victoryReveals: VictoryRevealEntry[];
  victoryFinished: boolean;
  /** Purely advisory tally from the server - the host isn't bound by it. */
  victoryVotes: VictoryVoteCounts;
}

type Listener = (state: AppState) => void;

const initialState: AppState = {
  connected: false,
  lobbyCode: null,
  myPlayerId: null,
  isHost: false,
  isSpectator: false,
  joined: false,
  isAnonymousSpectator: false,
  snapshot: null,
  wordView: null,
  wordOffer: null,
  cccOffer: null,
  config: null,
  chatMessages: [],
  cccDebugAvailable: false,
  availableWordLists: [{ id: "english", label: "English" }],
  victoryAchievements: [],
  victoryReveals: [],
  victoryFinished: false,
  victoryVotes: { playAgain: 0, returnToLobby: 0 },
};

class Store {
  private state: AppState = { ...initialState };
  private listeners = new Set<Listener>();

  getState(): AppState {
    return this.state;
  }

  setState(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  pushChatMessage(message: ChatMessageDTO): void {
    const chatMessages = [...this.state.chatMessages, message];
    if (chatMessages.length > 500) chatMessages.splice(0, chatMessages.length - 500);
    this.setState({ chatMessages });
  }
}

export const store = new Store();

export function myPlayer(state: AppState) {
  if (!state.myPlayerId || !state.snapshot) return null;
  return state.snapshot.players.find((p) => p.id === state.myPlayerId) ?? null;
}
